/**
 * The API client. One place that knows about fetch, error shapes and cookies.
 */
import type {
  ApiErrorBody,
  CallAssignment,
  CallResultInput,
  CallTarget,
  DayPlan,
  Goal,
  ProgressResponse,
  ReviewResponse,
  TodayResponse,
  UserProfile,
} from '../../shared/types';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...(init?.body ? { headers: { 'Content-Type': 'application/json' } } : {}),
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiRequestError(
      response.status,
      body?.error.code ?? 'unknown',
      body?.error.message ?? 'Something went wrong.',
      body?.error.details,
    );
  }
  return payload as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export interface HealthResponse {
  ok: boolean;
  app: string;
  aiConfigured: boolean;
  authConfigured: boolean;
}

export interface PlanResponse {
  date: string;
  plan: DayPlan | null;
  assignments: CallAssignment[];
  reused?: boolean;
  shortOfTargets?: boolean;
  rejectedIntents?: number;
}

export interface ResultResponse {
  assignment: CallAssignment | null;
  next: CallAssignment | null;
  dayComplete: boolean;
  completedCalls: number;
  plannedCalls: number;
}

export interface OnboardingResponse {
  profile: UserProfile;
  goals: Goal[];
  summary: string;
  plan: DayPlan | null;
  assignments: CallAssignment[];
  shortOfTargets: boolean;
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  session: () => request<{ authenticated: boolean }>('/session'),
  login: (password: string) => post<{ ok: true }>('/session', { password }),
  logout: () => request<{ ok: true }>('/session', { method: 'DELETE' }),

  profile: () => request<UserProfile>('/profile'),
  updateProfile: (
    body: Partial<Pick<UserProfile, 'name' | 'timezone' | 'dailyCallTarget' | 'searchLocation'>>,
  ) => patch<UserProfile>('/profile', body),
  onboard: (body: {
    why: string;
    comfortBaseline?: number | undefined;
    avoidedCallTypes?: string | undefined;
    dailyCallTarget: number;
    timezone?: string | undefined;
  }) => post<OnboardingResponse>('/onboarding', body),

  goals: () => request<Goal[]>('/goals'),
  createGoal: (body: { text: string; category?: Goal['category']; importance?: number }) =>
    post<Goal>('/goals', body),
  updateGoal: (id: string, body: Partial<Pick<Goal, 'text' | 'importance' | 'active'>>) =>
    patch<Goal>(`/goals/${id}`, body),
  deleteGoal: (id: string) => request<{ ok: true }>(`/goals/${id}`, { method: 'DELETE' }),

  targets: () => request<CallTarget[]>('/targets'),
  createTarget: (body: {
    businessName: string;
    phoneNumber: string;
    category: string;
    location?: string | undefined;
    notes?: string | undefined;
  }) => post<CallTarget>('/targets', body),
  updateTarget: (id: string, body: Partial<Pick<CallTarget, 'active' | 'phoneNumber'>>) =>
    patch<CallTarget>(`/targets/${id}`, body),
  deleteTarget: (id: string) => request<{ ok: true }>(`/targets/${id}`, { method: 'DELETE' }),

  today: (date?: string) => request<TodayResponse>(`/today${date ? `?date=${date}` : ''}`),
  start: (id: string) => post<CallAssignment>(`/assignments/${id}/start`),
  submitResult: (id: string, body: CallResultInput) =>
    post<ResultResponse>(`/assignments/${id}/result`, body),
  replace: (id: string) => post<CallAssignment>(`/assignments/${id}/replace`),

  plan: (date?: string) => request<PlanResponse>(`/plan${date ? `?date=${date}` : ''}`),
  generatePlan: (body?: { date?: string; force?: boolean }) => post<PlanResponse>('/plan', body),

  review: (date?: string) => request<ReviewResponse>(`/review${date ? `?date=${date}` : ''}`),
  generateReview: (body?: { date?: string; force?: boolean }) =>
    post<ReviewResponse>('/review', body),

  progress: () => request<ProgressResponse>('/progress'),
  reset: () => post<{ ok: true }>('/reset', { confirm: 'RESET' }),
};
